import {
  EInviteStatus,
  EInviteType,
  EUserStatus,
  type IInvite,
  type ISavedDocument,
  type IUser,
} from 'findef'
import type { FindexUser } from '@/types/models/user'
import { uniqueBy } from '@/utils/array'

export type ShareholderWithStatus = Omit<ISavedDocument<IUser, string>, 'status'> & {
  status?: EUserStatus | 'DECLINED'
}

// Merges base shareholders with pending/declined invitees so the UI can
// surface the full picture in one list. Pure — accepts pre-fetched data so
// both the per-resource path (getAssetShareholders) and the bulk
// tooltipSummary path can share the same projection. (DEV-4500)
export const mergeShareholdersWithInvites = (
  items: FindexUser[] | undefined,
  invites: ISavedDocument<IInvite, string>[] | undefined,
): ShareholderWithStatus[] => {
  const inviteList = invites || []

  const pending = inviteList
    .filter(
      (invite) =>
        invite.type === EInviteType.SHAREHOLDER &&
        invite.status === EInviteStatus.PENDING,
    )
    .map(
      (invite) =>
        ({
          ...invite.user,
          status: EUserStatus.PENDING,
        }) as ShareholderWithStatus,
    )

  const rejected = inviteList
    .filter(
      (invite) =>
        invite.type === EInviteType.SHAREHOLDER &&
        (invite.status === EInviteStatus.DECLINED ||
          invite.status === EInviteStatus.DELETED),
    )
    .map(
      (invite) =>
        ({
          ...invite.user,
          status: 'DECLINED' as const,
        }) as ShareholderWithStatus,
    )

  return uniqueBy<ShareholderWithStatus>(
    [...(items || []), ...pending, ...rejected],
    (user) => user.email,
  )
}
